import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { gestorVisivel } from '../lib/filtroGestor'

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

// Limiares de classificacao. Sao julgamento, nao regra da metodologia FCA —
// ajuste aqui se a Enterplak/RwTech definir criterios proprios.
const FOLGA_FROUXA = 0.25      // bateu todos os meses com >= 25% de margem -> meta facil
const GAP_POR_POUCO = 0.10     // falhou, mas por menos de 10% -> meta quase certa
const GAP_CRONICO = 0.25       // falhou por mais de 25% -> meta irreal ou processo quebrado
const VOLATIL = 0.40           // desvio-padrao da folga >= 40% -> resultado instavel
const MIN_PONTOS_CONFIANCA = 6 // abaixo disso a sugestao numerica e' base curta

// Mes de referencia = mes anterior (convencao da plataforma)
function mesReferencia() {
  const hoje = new Date()
  let ano = hoje.getFullYear()
  let mes = hoje.getMonth() // 0-11 => mes anterior em base 1
  if (mes === 0) { mes = 12; ano -= 1 }
  return { ano, mes }
}

// Janela de N meses terminando no mes de referencia, como indices ano*12+mes
function janela(qtdMeses) {
  const { ano, mes } = mesReferencia()
  const fim = ano * 12 + mes
  return { inicio: fim - qtdMeses + 1, fim, anoMin: Math.floor((fim - qtdMeses) / 12), anoMax: ano }
}

const idx = (ano, mes) => ano * 12 + mes
const rotulo = (ano, mes) => `${MESES[mes - 1]}/${String(ano).slice(2)}`

// Percentil com interpolacao linear
function percentil(valores, p) {
  if (!valores.length) return null
  const v = [...valores].sort((a, b) => a - b)
  if (v.length === 1) return v[0]
  const pos = (v.length - 1) * p
  const baixo = Math.floor(pos)
  const alto = Math.ceil(pos)
  if (baixo === alto) return v[baixo]
  return v[baixo] + (v[alto] - v[baixo]) * (pos - baixo)
}

const media = a => a.reduce((s, x) => s + x, 0) / a.length
function desvioPadrao(a) {
  if (a.length < 2) return 0
  const m = media(a)
  return Math.sqrt(media(a.map(x => (x - m) ** 2)))
}

function fmt(valor, casas) {
  if (valor == null || Number.isNaN(valor)) return '—'
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
}
const pct = v => (v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(0)}%`)

const CLASSES = {
  frouxa: { label: 'Meta frouxa', cls: 'badge-amber' },
  calibrada: { label: 'Bem calibrada', cls: 'badge-green' },
  instavel: { label: 'Resultado instável', cls: 'badge-gray' },
  cronica: { label: 'Falha crônica', cls: 'badge-red' },
  porPouco: { label: 'Falhou por pouco', cls: 'badge-blue' },
  oscilante: { label: 'Oscilante', cls: 'badge-gray' },
  insuficiente: { label: 'Dados insuficientes', cls: 'badge-gray' },
}

export default function AnalyticsMetas() {
  const { isSuperAdmin, selectedCompany, filtroGestor, setFiltroGestor } = useAuth()
  const [meses, setMeses] = useState(3)
  const [filtroClasse, setFiltroClasse] = useState('')
  const [gestores, setGestores] = useState([])
  const [linhas, setLinhas] = useState([])
  const [semFca, setSemFca] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let q = supabase.from('profiles').select('id, nome').order('nome')
    if (isSuperAdmin && selectedCompany) q = q.eq('company_id', selectedCompany.id)
    q.then(({ data }) => setGestores(data || []))
  }, [isSuperAdmin, selectedCompany])

  // Filtro de gestor compartilhado com as outras telas (ver AuthContext).
  const gestorSel = gestorVisivel(filtroGestor, gestores)

  // gestores entra nas dependencias porque gestorMap e' montado a partir dele
  useEffect(() => { fetchData() }, [meses, gestorSel, selectedCompany, gestores])

  async function fetchData() {
    setLoading(true)
    const { inicio, fim, anoMin, anoMax } = janela(meses)

    // Taticos entram aqui de proposito: calibrar meta e' util para eles tambem,
    // e o fluxo de FCA nao muda, entao um NOK tatico sem FCA continua sendo
    // pendencia. Ficam identificados pela etiqueta "tático".
    let indsQuery = supabase.from('indicadores')
      .select('id, nome, unidade, objetivo, casas_decimais, gestor_id, apenas_tatico')
    if (gestorSel) indsQuery = indsQuery.eq('gestor_id', gestorSel)
    if (isSuperAdmin && selectedCompany) indsQuery = indsQuery.eq('company_id', selectedCompany.id)

    const [{ data: inds }, { data: res }, { data: fcas }] = await Promise.all([
      indsQuery,
      supabase.from('resultados').select('indicador_id, ano, mes, meta, resultado')
        .gte('ano', anoMin).lte('ano', anoMax),
      supabase.from('fcas').select('indicador_id, ano, mes')
        .gte('ano', anoMin).lte('ano', anoMax),
    ])

    if (!inds || !res) { setLinhas([]); setSemFca([]); setLoading(false); return }

    const indMap = Object.fromEntries(inds.map(i => [i.id, i]))
    const gestorMap = Object.fromEntries(gestores.map(g => [g.id, g.nome]))
    const temFca = new Set((fcas || []).map(f => `${f.indicador_id}|${f.ano}|${f.mes}`))

    // Mantem so o que esta na janela, tem meta valida e resultado lancado
    const naJanela = res.filter(r => {
      const k = idx(r.ano, r.mes)
      return k >= inicio && k <= fim &&
        indMap[r.indicador_id] &&
        r.resultado != null && r.meta != null && r.meta !== 0
    })

    // ---------- Insight 5: NOK sem FCA ----------
    const pendentes = []
    for (const r of naJanela) {
      const ind = indMap[r.indicador_id]
      const ok = ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta
      if (ok) continue
      if (temFca.has(`${r.indicador_id}|${r.ano}|${r.mes}`)) continue
      const gap = ind.objetivo === 'maior'
        ? (r.resultado - r.meta) / Math.abs(r.meta)
        : (r.meta - r.resultado) / Math.abs(r.meta)
      pendentes.push({
        key: `${r.indicador_id}-${r.ano}-${r.mes}`,
        indicador: ind.nome,
        gestor: gestorMap[ind.gestor_id] || '—',
        periodo: rotulo(r.ano, r.mes),
        ordem: idx(r.ano, r.mes),
        meta: r.meta, resultado: r.resultado, gap,
        casas: ind.casas_decimais ?? 0,
        unidade: ind.unidade || '',
        tatico: !!ind.apenas_tatico,
      })
    }
    pendentes.sort((a, b) => a.gap - b.gap || b.ordem - a.ordem)

    // ---------- Insight 1: sugestao numerica de meta ----------
    const porIndicador = {}
    for (const r of naJanela) {
      (porIndicador[r.indicador_id] ||= []).push(r)
    }

    const out = []
    for (const [indicadorId, pontos] of Object.entries(porIndicador)) {
      const ind = indMap[indicadorId]
      const maior = ind.objetivo === 'maior'

      const folgas = pontos.map(p => maior
        ? (p.resultado - p.meta) / Math.abs(p.meta)
        : (p.meta - p.resultado) / Math.abs(p.meta))
      const hits = pontos.filter(p => (maior ? p.resultado >= p.meta : p.resultado <= p.meta)).length
      const n = pontos.length
      const folgaMedia = media(folgas)
      const volatilidade = desvioPadrao(folgas)

      let classe
      if (n < 2) classe = 'insuficiente'
      else if (hits === n && folgaMedia >= FOLGA_FROUXA) classe = 'frouxa'
      else if (hits === n) classe = 'calibrada'
      else if (volatilidade >= VOLATIL) classe = 'instavel'
      else if (hits === 0 && folgaMedia <= -GAP_CRONICO) classe = 'cronica'
      else if (hits === 0 && folgaMedia > -GAP_POR_POUCO) classe = 'porPouco'
      else if (hits === 0) classe = 'cronica'
      else classe = 'oscilante'

      // Meta sugerida = valor que teria sido atingido em ~75% dos meses da janela
      const resultados = pontos.map(p => p.resultado)
      const sugestao = percentil(resultados, maior ? 0.25 : 0.75)

      // Meta vigente = a do mes mais recente da janela
      const maisRecente = pontos.reduce((a, b) => (idx(a.ano, a.mes) > idx(b.ano, b.mes) ? a : b))
      const metaAtual = maisRecente.meta

      const bateriaCom = sugestao == null ? null
        : pontos.filter(p => (maior ? p.resultado >= sugestao : p.resultado <= sugestao)).length

      const delta = sugestao == null || !metaAtual ? null : (sugestao - metaAtual) / Math.abs(metaAtual)
      let direcao = 'manter'
      if (delta != null && Math.abs(delta) >= 0.05) direcao = delta > 0 ? 'subir' : 'baixar'
      // Para objetivo 'menor', subir o numero afrouxa a meta e baixar aperta.
      const afrouxa = maior ? direcao === 'baixar' : direcao === 'subir'

      out.push({
        key: indicadorId,
        indicador: ind.nome,
        gestor: gestorMap[ind.gestor_id] || '—',
        unidade: ind.unidade || '',
        casas: ind.casas_decimais ?? 0,
        objetivo: ind.objetivo,
        tatico: !!ind.apenas_tatico,
        n, hits, folgaMedia, volatilidade, classe,
        metaAtual, sugestao, delta, direcao, afrouxa, bateriaCom,
        confianca: n >= MIN_PONTOS_CONFIANCA ? 'boa' : 'curta',
      })
    }

    const ordemClasse = { frouxa: 0, cronica: 1, oscilante: 2, porPouco: 3, instavel: 4, calibrada: 5, insuficiente: 6 }
    out.sort((a, b) => ordemClasse[a.classe] - ordemClasse[b.classe] || a.indicador.localeCompare(b.indicador))

    setLinhas(out)
    setSemFca(pendentes)
    setLoading(false)
  }

  const visiveis = filtroClasse ? linhas.filter(l => l.classe === filtroClasse) : linhas
  const kpis = [
    { label: 'Indicadores avaliados', val: linhas.length, color: 'var(--text)' },
    { label: 'Metas frouxas', val: linhas.filter(l => l.classe === 'frouxa').length, color: 'var(--amber)' },
    { label: 'Falhas crônicas', val: linhas.filter(l => l.classe === 'cronica').length, color: 'var(--red)' },
    { label: 'NOK sem FCA', val: semFca.length, color: semFca.length > 0 ? 'var(--red)' : 'var(--green)' },
  ]

  const { ano: anoRef, mes: mesRef } = mesReferencia()

  return (
    <div>
      <div className="page-title">
        <div>
          <h2>Meta Analytics</h2>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
            Calibração de metas nos últimos {meses} meses — até {rotulo(anoRef, mesRef)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={gestorSel} onChange={e => setFiltroGestor(e.target.value)} style={{ width: 'auto', minWidth: 160 }}>
            <option value="">Todos os gestores</option>
            {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
          </select>
          <select value={filtroClasse} onChange={e => setFiltroClasse(e.target.value)} style={{ width: 'auto', minWidth: 170 }}>
            <option value="">Todas as situações</option>
            {Object.entries(CLASSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={meses} onChange={e => setMeses(Number(e.target.value))} style={{ width: 'auto' }}>
            <option value={3}>Últimos 3 meses</option>
            <option value={6}>Últimos 6 meses</option>
            <option value={12}>Últimos 12 meses</option>
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {kpis.map(m => (
          <div key={m.label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '16px 20px', boxShadow: 'var(--shadow)'
          }}>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: m.color }}>{loading ? '—' : m.val}</div>
          </div>
        ))}
      </div>

      {/* Insight 1 — sugestao de ajuste de meta */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, marginBottom: 4 }}>Sugestão de ajuste de meta</h3>
        <p style={{ color: 'var(--text2)', fontSize: 12, marginBottom: 14 }}>
          A meta sugerida é o valor que teria sido atingido em cerca de 75% dos meses da janela.
          É um ponto de partida para a discussão, não uma decisão automática.
        </p>

        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : visiveis.length === 0 ? (
          <div className="empty-state">Nenhum indicador com dados suficientes no período.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Indicador</th>
                  <th>Gestor</th>
                  <th style={{ textAlign: 'center' }}>Bateu</th>
                  <th style={{ textAlign: 'right' }}>Folga média</th>
                  <th style={{ textAlign: 'right' }}>Meta atual</th>
                  <th style={{ textAlign: 'right' }}>Meta sugerida</th>
                  <th style={{ textAlign: 'center' }}>Situação</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map(l => {
                  const c = CLASSES[l.classe]
                  const mostraSugestao = l.classe !== 'instavel' && l.classe !== 'insuficiente' && l.direcao !== 'manter'
                  return (
                    <tr key={l.key}>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {l.indicador}
                          {l.tatico && <span className="badge badge-amber" style={{ marginLeft: 6 }}>tático</span>}
                        </div>
                        {l.unidade && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l.unidade}</div>}
                      </td>
                      <td style={{ color: 'var(--text2)', fontSize: 12 }}>{l.gestor}</td>
                      <td style={{ textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 12 }}>
                        {l.hits}/{l.n}
                      </td>
                      <td style={{
                        textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12,
                        color: l.folgaMedia >= 0 ? 'var(--green)' : 'var(--red)'
                      }}>
                        {pct(l.folgaMedia)}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12 }}>
                        {fmt(l.metaAtual, l.casas)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {mostraSugestao ? (
                          <>
                            <div style={{
                              fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600,
                              color: l.afrouxa ? 'var(--amber)' : 'var(--green)'
                            }}>
                              {l.direcao === 'subir' ? '▲' : '▼'} {fmt(l.sugestao, l.casas)}
                            </div>
                            {/* Em indicadores de objetivo 'menor' a seta inverte de sentido:
                                baixar o numero aperta a meta. A palavra evita a ambiguidade
                                e nao depende so' da cor. */}
                            <div style={{
                              fontSize: 10, fontWeight: 600,
                              color: l.afrouxa ? 'var(--amber)' : 'var(--green)'
                            }}>
                              {l.afrouxa ? 'afrouxa' : 'aperta'}
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                              teria batido {l.bateriaCom}/{l.n}
                              {l.confianca === 'curta' && ' · base curta'}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--text3)', fontSize: 12 }}>manter</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${c.cls}`}>{c.label}</span>
                        {l.classe === 'cronica' && (
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                            revisar meta <em>ou</em> processo
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="alert alert-info" style={{ marginTop: 14, fontSize: 12 }}>
          <strong>Afrouxar meta exige cuidado.</strong> Quando um indicador falha de forma crônica,
          a tela não consegue distinguir meta irreal de processo quebrado — os dois produzem o mesmo
          padrão. Trate a sugestão para baixo como hipótese a investigar, nunca como ajuste automático.
        </div>
      </div>

      {/* Insight 5 — NOK sem FCA */}
      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 4 }}>Metas não atingidas sem FCA</h3>
        <p style={{ color: 'var(--text2)', fontSize: 12, marginBottom: 14 }}>
          Meses em que o resultado ficou fora da meta e nenhuma análise foi aberta.
        </p>

        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : semFca.length === 0 ? (
          <div className="empty-state">Todos os desvios do período têm FCA aberto. 🎉</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Indicador</th>
                  <th>Gestor</th>
                  <th>Período</th>
                  <th style={{ textAlign: 'right' }}>Meta</th>
                  <th style={{ textAlign: 'right' }}>Resultado</th>
                  <th style={{ textAlign: 'right' }}>Desvio</th>
                </tr>
              </thead>
              <tbody>
                {semFca.map(p => (
                  <tr key={p.key}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {p.indicador}
                        {p.tatico && <span className="badge badge-amber" style={{ marginLeft: 6 }}>tático</span>}
                      </div>
                      {p.unidade && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{p.unidade}</div>}
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{p.gestor}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{p.periodo}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12 }}>{fmt(p.meta, p.casas)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12 }}>{fmt(p.resultado, p.casas)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--red)' }}>
                      {pct(p.gap)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
